import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { agents as agentsApi } from "./api";

const SELECTED_KEY = "linkia_selected_agent";
const AgentsContext = createContext(null);

export function AgentsProvider({ children }) {
    const [agents, setAgents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedAgentId, setSelectedAgentIdState] = useState(
        () => localStorage.getItem(SELECTED_KEY) || null
    );
    // dialog: { open, view: "list"|"form", editing: agent|null }
    const [dialog, setDialog] = useState({ open: false, view: "list", editing: null });

    const refresh = useCallback(async () => {
        if (!localStorage.getItem("linkia_token")) { setLoading(false); return; }
        try {
            const list = await agentsApi.list();
            setAgents(Array.isArray(list) ? list : []);
        } catch { /* silencioso */ }
        finally { setLoading(false); }
    }, []);

    useEffect(() => {
        refresh();
        const onAuth = () => refresh();
        window.addEventListener("linkia-auth-changed", onAuth);
        return () => window.removeEventListener("linkia-auth-changed", onAuth);
    }, [refresh]);

    const setSelectedAgentId = useCallback((id) => {
        setSelectedAgentIdState(id);
        if (id) localStorage.setItem(SELECTED_KEY, id);
        else localStorage.removeItem(SELECTED_KEY);
    }, []);

    const createAgent = useCallback(async (payload) => {
        const a = await agentsApi.create(payload);
        setAgents((prev) => [a, ...prev]);
        return a;
    }, []);

    const updateAgent = useCallback(async (id, payload) => {
        const a = await agentsApi.update(id, payload);
        setAgents((prev) => prev.map((x) => (x.id === id ? a : x)));
        return a;
    }, []);

    const deleteAgent = useCallback(async (id) => {
        await agentsApi.remove(id);
        setAgents((prev) => prev.filter((x) => x.id !== id));
        setSelectedAgentIdState((cur) => {
            if (cur === id) { localStorage.removeItem(SELECTED_KEY); return null; }
            return cur;
        });
    }, []);

    const openAgentsDialog = useCallback((view = "list", editing = null) => {
        setDialog({ open: true, view, editing });
    }, []);
    const closeAgentsDialog = useCallback(() => {
        setDialog((d) => ({ ...d, open: false }));
    }, []);

    const selectedAgent = useMemo(
        () => agents.find((a) => a.id === selectedAgentId) || null,
        [agents, selectedAgentId]
    );

    const value = useMemo(() => ({
        agents, loading, refresh,
        createAgent, updateAgent, deleteAgent,
        selectedAgentId, selectedAgent, setSelectedAgentId,
        dialog, openAgentsDialog, closeAgentsDialog,
    }), [agents, loading, refresh, createAgent, updateAgent, deleteAgent,
        selectedAgentId, selectedAgent, setSelectedAgentId, dialog, openAgentsDialog, closeAgentsDialog]);

    return <AgentsContext.Provider value={value}>{children}</AgentsContext.Provider>;
}

export function useAgents() {
    const ctx = useContext(AgentsContext);
    if (!ctx) throw new Error("useAgents precisa estar dentro de <AgentsProvider>");
    return ctx;
}
